import { Check, Sparkles, TrendingUp } from 'lucide-react';

/**
 * The decorative report mock that fills the right half of the hero.
 *
 * It exists because the hero was previously text on the left and nothing at all
 * on the right — a large empty column above the fold. This shows what the tool
 * gives you before you have typed anything, which is a fair bit more persuasive
 * than describing it.
 *
 * Three things about it are deliberate and worth not undoing:
 *
 *  - **`aria-hidden`, and no headings.** It is a picture of a report, not a
 *    report. Announcing a made-up score of 93 to a screen-reader user, or
 *    putting fake sections into the document outline, would be actively
 *    misleading.
 *  - **It is not `ScoreRing`.** Reusing the real component would drag in the
 *    string "out of 100", and the empty state is asserted not to contain it —
 *    `aria-hidden` does not hide text from a testing-library text query, and it
 *    would not hide it from find-in-page either.
 *  - **The section labels are not the real ones.** "First impression" rather
 *    than "Headline", so nothing in here can be mistaken for the live form's own
 *    sections by a human or by a query.
 *
 * The numbers are fixed, not random: a static example, not a claim about what
 * anyone will score.
 */

const SIZE = 148;
const STROKE = 12;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
/** The sample score. Drawn at 93%, and shown as 93. */
const SAMPLE = 93;

const ROWS = [
  { label: 'First impression', value: 96 },
  { label: 'Your story', value: 88 },
  { label: 'Searchability', value: 94 },
];

export function HeroPreview() {
  return (
    <div aria-hidden="true" className="relative select-none">
      {/* Two stacked cards, the back one rotated a few degrees, so the group
          reads as a deck rather than a screenshot. */}
      <div className="absolute inset-x-6 top-6 h-full rotate-3 rounded-card border border-line bg-card/70 shadow-card" />

      <div className="surface-card relative -rotate-1 p-6">
        <div className="flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-pill bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700 dark:bg-brand-950 dark:text-brand-100">
            <Sparkles className="size-3.5" />
            Your report
          </span>
          <span className="inline-flex items-center gap-1 rounded-pill bg-success-50 px-2.5 py-1 text-xs font-semibold text-success-700 dark:bg-success-950 dark:text-success-100">
            <TrendingUp className="size-3.5" />
            +31 this week
          </span>
        </div>

        <div className="mt-5 flex items-center gap-5">
          <div className="relative shrink-0" style={{ width: SIZE, height: SIZE }}>
            <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} className="-rotate-90">
              <defs>
                {/* A gradient stroke, which the real ring deliberately does not
                    use — there, the flat band colour is what tells you how bad
                    things are. Here there is nothing to communicate. */}
                <linearGradient id="hero-preview-arc" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="var(--color-brand-500)" />
                  <stop offset="55%" stopColor="var(--color-hype-500)" />
                  <stop offset="100%" stopColor="var(--color-accent-400)" />
                </linearGradient>
              </defs>
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke="var(--ui-sunken)"
                strokeWidth={STROKE}
              />
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke="url(#hero-preview-arc)"
                strokeWidth={STROKE}
                strokeLinecap="round"
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={CIRCUMFERENCE * (1 - SAMPLE / 100)}
              />
            </svg>

            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="gradient-text text-5xl font-bold tracking-tight tabular-nums">
                {SAMPLE}
              </span>
              {/* Not "out of 100" — see the note at the top of this file. */}
              <span className="text-[0.6rem] font-semibold tracking-[0.14em] text-subtle uppercase">
                /100
              </span>
            </div>

            <span
              className="absolute bottom-0 left-1/2 grid size-10 -translate-x-1/2 translate-y-1/4 place-items-center rounded-pill border-2 border-card text-base font-bold text-white shadow-lifted"
              style={{ backgroundImage: 'var(--gradient-cta)' }}
            >
              A
            </span>
          </div>

          <div className="min-w-0 flex-1 space-y-3">
            {ROWS.map((row) => (
              <div key={row.label}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-xs font-medium text-muted">{row.label}</span>
                  <span className="text-xs font-semibold text-strong tabular-nums">
                    {row.value}
                  </span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-pill bg-sunken">
                  <span
                    className="block h-full rounded-pill"
                    style={{ width: `${row.value}%`, backgroundImage: 'var(--gradient-fill)' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <ul className="mt-5 space-y-2 border-t border-line pt-4">
          {['Add a number to your top bullet', 'Name the tools recruiters filter on'].map(
            (item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-muted">
                {/* success-600, not 500: a white glyph on success-500 is 2.28:1,
                    which is faint at 12px even for decoration. */}
                <span className="mt-0.5 grid size-4 shrink-0 place-items-center rounded-pill bg-success-600 text-white">
                  <Check className="size-3" />
                </span>
                {item}
              </li>
            )
          )}
        </ul>
      </div>
    </div>
  );
}
